import { format } from 'date-fns';

import { formatDisply, resolveTimeZoneForIntl } from '../../../shared/utils';
/**
 * Global date format configuration supports only these 3 formats:
 * 1. DD-MMM-YYYY (e.g. 05-Feb-2026)
 * 2. MMM DD, YYYY (e.g. Feb 05, 2026)
 * 3. MM-DD-YYYY (e.g. 02-05-2026)
 *
 * Keys are canonical (date-fns style). API format (DD, YYYY) is normalized for lookup.
 * Values are date-fns-compatible display formats that include time.
 */
export const DATE_FORMAT_MAP = {
  'dd-MMM-yyyy': 'dd-MMM-yyyy, h:mm aa',
  'MMM dd, yyyy': 'MMM dd, yyyy, h:mm aa',
  'MM-dd-yyyy': 'MM-dd-yyyy, h:mm aa',
} as const;

export type DateFormatKey = keyof typeof DATE_FORMAT_MAP;

export const DEFAULT_DISPLAY_FORMAT = 'MMM dd, yyyy, h:mm aa' as const;

/** Formats supported by design-system DateAndTimePicker (with time). */
const DATE_PICKER_SUPPORTED_FORMATS = [
  'MM-dd-yyyy, h:mm aa',
  'dd-MM-yyyy, h:mm aa',
] as const;

/**
 * Normalizes API format tokens (DD, YYYY) to date-fns tokens (dd, yyyy) for lookup.
 */
function normalizeDateFormatKey(key: string): string {
  return key.replace(/DD/g, 'dd').replace(/YYYY/g, 'yyyy');
}

/**
 * True when a format key can be resolved by Activity Log date format map.
 * Accepts both API-style tokens (DD/YYYY) and date-fns tokens (dd/yyyy).
 */
export function isSupportedGlobalDateFormatKey(
  formatKey: string | undefined
): boolean {
  if (!formatKey) {
    return false;
  }

  const normalized = normalizeDateFormatKey(formatKey);

  return (Object.keys(DATE_FORMAT_MAP) as DateFormatKey[]).some(
    (k) => normalizeDateFormatKey(k) === normalized
  );
}

/**
 * Returns the display format string for date-fns/format when given a global date format key.
 * Supports only: DD-MMM-YYYY, MMM DD YYYY, MM-DD-YYYY (and lowercase variants).
 */
export function getDisplayFormatForGlobalDateFormat(
  globalDateFormat: string | undefined
): (typeof DATE_FORMAT_MAP)[DateFormatKey] {
  if (!globalDateFormat) {
    return DEFAULT_DISPLAY_FORMAT;
  }

  const normalized = normalizeDateFormatKey(globalDateFormat);
  const key = (Object.keys(DATE_FORMAT_MAP) as DateFormatKey[]).find(
    (k) => normalizeDateFormatKey(k) === normalized
  );

  if (key) {
    return DATE_FORMAT_MAP[key];
  }

  return DEFAULT_DISPLAY_FORMAT;
}

/**
 * Returns a format supported by design-system DateAndTimePicker.
 * MM-dd-yyyy is supported; dd-MMM-yyyy and MMM dd, yyyy fall back to closest picker format.
 */
export function getDateAndTimePickerFormatForGlobalDateFormat(
  globalDateFormat: string | undefined
): (typeof DATE_PICKER_SUPPORTED_FORMATS)[number] {
  const displayFormat = getDisplayFormatForGlobalDateFormat(globalDateFormat);

  if (
    (DATE_PICKER_SUPPORTED_FORMATS as readonly string[]).includes(displayFormat)
  ) {
    return displayFormat as (typeof DATE_PICKER_SUPPORTED_FORMATS)[number];
  }

  if (displayFormat.startsWith('dd-')) {
    return 'dd-MM-yyyy, h:mm aa';
  }

  return 'MM-dd-yyyy, h:mm aa';
}

/**
 * Formats a date-like value using the provided globalDateFormat and DATE_FORMAT_MAP.
 * Falls back gracefully when value or format are invalid.
 */
export const formatDateByGlobal = (
  value: unknown,
  globalDateFormat?: string
): string => {
  let dt: Date | null = null;

  if (typeof value === 'number' || typeof value === 'string') {
    dt = new Date(value);
  } else if (value instanceof Date) {
    dt = value;
  }

  if (!dt || Number.isNaN(dt.getTime())) {
    return '-';
  }

  try {
    const displayFmt = getDisplayFormatForGlobalDateFormat(globalDateFormat);

    return format(dt, displayFmt);
  } catch {
    return format(dt, DEFAULT_DISPLAY_FORMAT);
  }
};

/** Month abbrev to 2-digit for MM-dd-yyyy format */
const MONTH_ABBREV_TO_NUM: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

/**
 * Formats an ISO date in a specific timezone using the configured display format.
 * Supports dd-MMM-yyyy, MMM dd yyyy, MM-dd-yyyy (with time).
 */
export const formatInTimeZoneWithDisplayFormat = (
  iso: string | undefined,
  timeZone: string,
  displayFormat: string
): string => {
  if (!iso || !timeZone) {
    return '-';
  }

  try {
    const { timeZone: ianaTimeZone, offsetMinutes } =
      resolveTimeZoneForIntl(timeZone);
    const baseDate = new Date(iso);
    const dateForFmt =
      offsetMinutes !== 0
        ? new Date(baseDate.getTime() + offsetMinutes * 60_000)
        : baseDate;

    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: ianaTimeZone,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(dateForFmt);

    const map: Record<string, string> = {};

    parts.forEach((p) => {
      if (p.type !== 'literal') {
        map[p.type] = p.value;
      }
    });

    const day = map.day || '';
    const monthAbbrev = map.month || '';
    const monthNum =
      monthAbbrev in MONTH_ABBREV_TO_NUM
        ? MONTH_ABBREV_TO_NUM[monthAbbrev]
        : '01';
    const year = map.year || '';
    const hour24 = Number(map.hour || '0');
    const minute = map.minute || '00';

    const is12h = displayFormat.includes('h:mm');
    const displayHour = is12h
      ? String(hour24 % 12 || 12)
      : String(hour24).padStart(2, '0');
    const ampm = hour24 < 12 ? 'AM' : 'PM';
    const timePart = is12h
      ? `${displayHour}:${minute} ${ampm}`
      : `${displayHour}:${minute}`;

    let datePart: string;

    if (displayFormat.startsWith('dd-MMM')) {
      datePart = `${day}-${monthAbbrev}-${year}`;
    } else if (displayFormat.startsWith('MMM')) {
      datePart = `${monthAbbrev} ${day}, ${year}`;
    } else if (displayFormat.startsWith('MM-dd')) {
      datePart = `${monthNum}-${day}-${year}`;
    } else {
      datePart = `${day}-${monthAbbrev}-${year}`;
    }

    return `${datePart}, ${timePart}`;
  } catch {
    return '-';
  }
};

/**
 * Formats a date range using the configured display format when provided.
 * Falls back to Intl.DateTimeFormat when displayFormat is not provided.
 */
export const formatRange = (
  startIso: string,
  endIso: string,
  tz: string,
  displayFormat?: string
): string => {
  const start = new Date(startIso);
  const end = new Date(endIso);

  if (displayFormat) {
    const startStr = format(start, displayFormat);
    const endStr = format(end, displayFormat);

    return `${startStr} (${tz}) to ${endStr} (${tz})`;
  }

  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  const fmt = new Intl.DateTimeFormat(undefined, opts);
  const startStr = fmt.format(start);
  const endStr = fmt.format(end);

  return `${startStr} (${tz}) to ${endStr} (${tz})`;
};

/**
 * Formats a date range in a specific timezone using the configured display format when provided.
 */
export const formatRangeInTimeZone = (
  startIso: string,
  endIso: string,
  timeZone: string,
  displayFormat?: string
): string => {
  if (displayFormat) {
    const startStr = formatInTimeZoneWithDisplayFormat(
      startIso,
      timeZone,
      displayFormat
    );
    const endStr = formatInTimeZoneWithDisplayFormat(
      endIso,
      timeZone,
      displayFormat
    );

    return `${startStr} (${timeZone}) to ${endStr} (${timeZone})`;
  }

  const { timeZone: ianaTimeZone, offsetMinutes } =
    resolveTimeZoneForIntl(timeZone);

  const start = new Date(startIso);
  const end = new Date(endIso);

  const startForFmt =
    offsetMinutes !== 0
      ? new Date(start.getTime() + offsetMinutes * 60_000)
      : start;
  const endForFmt =
    offsetMinutes !== 0
      ? new Date(end.getTime() + offsetMinutes * 60_000)
      : end;

  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ianaTimeZone,
  };

  const fmt = new Intl.DateTimeFormat(undefined, opts);
  const startStr = fmt.format(startForFmt);
  const endStr = fmt.format(endForFmt);

  return `${startStr} (${timeZone}) to ${endStr} (${timeZone})`;
};

/**
 * Formats the duration between two ISO timestamps into a compact human string.
 * - If >= 1 hour: "Hh Mm"
 * - Else: "Mm Ss"
 */
export const formatDurationHms = (startIso: string, endIso: string): string => {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  const ms = Number.isFinite(end - start) ? Math.max(end - start, 0) : 0;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m ${seconds}s`;
};

/**
 * Converts a local Date to a UTC ISO-8601 string (Z suffix).
 * Returns null when the input is not a valid Date instance.
 *
 * @param d - Date value to convert (or null/undefined)
 * @returns ISO string in UTC or null when input is not a Date
 */
export const toUtcIso = (d: Date | null | undefined) =>
  d instanceof Date ? d.toISOString() : null;

/**
 * Extracts a user-readable error message from common API error shapes
 * (e.g., RTK Query/axios error objects). Falls back to the provided
 * default when no message can be derived.
 *
 * @param e - Unknown error object that may contain data/message
 * @param fallback - Message to use when nothing usable is found
 * @returns Derived error message string
 */
export const extractApiErrorMessage = (
  e: unknown,
  fallback: string
): string => {
  let message = fallback;

  if (e && typeof e === 'object') {
    const err = e as { data?: unknown; message?: unknown };

    if (typeof err.data === 'string') {
      message = err.data;
    } else if (
      err.data &&
      typeof (err.data as { message?: unknown }).message === 'string'
    ) {
      message = (err.data as { message: string }).message;
    } else if (typeof err.message === 'string') {
      message = err.message;
    }
  }

  return message;
};

export const formatEntitlementSeverityLabel = (
  value: unknown,
  l: (key: string) => string
): string => {
  const raw = typeof value === 'string' ? value.trim() : '';

  if (!raw) {
    return '-';
  }

  switch (raw.toUpperCase()) {
    case 'LOW':
      return l('Low');
    case 'MEDIUM':
      return l('Medium');
    case 'HIGH':
      return l('High');
    case 'CRITICAL':
      return l('Critical');
    default:
      return formatDisply(raw) ?? '-';
  }
};

export const formatChangeIndicatorLabel = (value: unknown): string => {
  const raw = typeof value === 'string' ? value.trim() : '';

  if (!raw) {
    return '-';
  }

  switch (raw.toUpperCase()) {
    case 'CREATE':
      return 'Create';
    case 'INSERT':
      return 'Insert';
    case 'UPDATE':
      return 'Update';
    case 'DELETE':
      return 'Delete';
    case 'JOURNAL':
      return 'Journal';
    default:
      return formatDisply(raw) ?? '-';
  }
};

/**
 * Reads HTTP status from RTK Query / baseQuery error objects that expose `status`
 * as a number (e.g. RTK Query errors from the axios base query).
 */
export function getQueryErrorHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const { status } = error as { status?: unknown };

  return typeof status === 'number' ? status : undefined;
}

/**
 * When the base query fails with HTTP 412, returns `message` from the error payload
 * (e.g. `{ status: 412, message: "..." }` on `error.data`).
 */
export function getHttp412ErrorResponseMessage(
  error: unknown
): string | undefined {
  if (getQueryErrorHttpStatus(error) !== 412) {
    return undefined;
  }

  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const { data } = error as { data?: unknown };

  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const { message } = data as { message?: unknown };

  if (typeof message === 'string') {
    const trimmed = message.trim();

    return trimmed.length > 0 ? trimmed : undefined;
  }

  return undefined;
}

/**
 * Navigate back to the source page based on query params.
 * Supports:
 * - from=sessions -> PAM sessions
 * - from=sessions -> control center sessions
 * - from=req-history&requestId=<digits> -> request history detail (listgen2)
 * - from=req-history-gsp -> Request History GSP page
 * - from=email -> request home
 * Falls back to request home.
 */
export const navigateBackToSource = (): void => {
  const params = new URLSearchParams(window.location.search);
  const fromParam = params.get('from');
  const validFrom = [
    'sessions',
    'req-history',
    'req-history-gsp',
    'email',
  ] as const;
  const from = validFrom.includes(fromParam as (typeof validFrom)[number])
    ? (fromParam as (typeof validFrom)[number])
    : null;
  const requestIdParam = params.get('requestId');
  const safeRequestId =
    requestIdParam && /^\d+$/.test(requestIdParam)
      ? encodeURIComponent(requestIdParam)
      : null;

  let targetPath = '/ECMv6/request/requestHome';

  if (from === 'sessions') {
    targetPath = '/ECMv6/control/controlCenter/sessions';
  } else if (from === 'req-history' && safeRequestId) {
    targetPath = `/ECMv6/request/requestHistory/${safeRequestId}?goto=listgen2`;
  } else if (from === 'req-history-gsp') {
    targetPath = '/ECM/jbpmworkflowmanagement/showmyhistoryrequests';
  } else if (from === 'email') {
    targetPath = '/ECMv6/request/requestHome';
  }

  window.location.replace(targetPath);
};
